import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { cn } from '@/lib/utils'
import {
  MAIL_FRAME_MAX_HEIGHT,
  MAIL_FRAME_SANDBOX,
  MAIL_HEIGHT_MESSAGE,
  buildMailSrcDoc,
  mailFrameCss,
  readMailFrameTheme,
  sanitizeMailHtml,
  type MailFrameTheme,
} from './mailFrame'
import { useMailFontCss } from './mailFont'

/**
 * The theme snapshot, cached per module: getComputedStyle runs once per theme
 * change (root class/style or window resize), not on every render of every frame.
 */
let themeCache: string | null = null

function subscribeTheme(onChange: () => void): () => void {
  const notify = () => {
    themeCache = null
    onChange()
  }
  const observer = new MutationObserver(notify)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'style', 'data-theme'],
  })
  // The root font size follows the window width (index.css, from 2200px).
  window.addEventListener('resize', notify)
  return () => {
    observer.disconnect()
    window.removeEventListener('resize', notify)
  }
}

const themeSnapshot = () => (themeCache ??= JSON.stringify(readMailFrameTheme()))

/** The resolved theme; re-read when the root's class (light/dark) or the window size changes. */
function useMailFrameTheme(): MailFrameTheme {
  const key = useSyncExternalStore(subscribeTheme, themeSnapshot)
  return useMemo(() => JSON.parse(key) as MailFrameTheme, [key])
}

interface HeightMessage {
  type: string
  id: string
  height: number
}

function isHeightMessage(data: unknown): data is HeightMessage {
  if (typeof data !== 'object' || data === null) return false
  const message = data as Partial<HeightMessage>
  return (
    message.type === MAIL_HEIGHT_MESSAGE &&
    typeof message.id === 'string' &&
    typeof message.height === 'number' &&
    Number.isFinite(message.height)
  )
}

/**
 * Inbound mail HTML, rendered safely: sanitised with DOMPurify and shown only
 * inside a sandboxed iframe (opaque origin, no forms, CSP that runs nothing but
 * the frame's own height script). The mail never enters the app's DOM. The
 * frame reports its height with postMessage; only messages from this frame's
 * own window are accepted. Use it for every mail body that comes from outside;
 * notes written in the app keep RichTextView.
 */
export function SandboxedMailHtml({
  html,
  title,
  className,
}: {
  html: string
  /** Accessible name of the frame, e.g. the subject. */
  title: string
  /** Classes for the wrapper around the frame (e.g. a white paper surface). */
  className?: string
}) {
  const id = useId()
  const clean = useMemo(() => sanitizeMailHtml(html), [html])
  const theme = useMailFrameTheme()
  const css = useMemo(() => mailFrameCss(theme), [theme])
  const fontCss = useMailFontCss()
  const srcDoc = useMemo(
    () => (fontCss === null ? null : buildMailSrcDoc(clean, `${fontCss}\n${css}`, id)),
    [clean, css, fontCss, id],
  )
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState<number | null>(null)

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const frame = frameRef.current
      // Only this frame's own window counts; any other window (another frame, a
      // popup, the app itself) is ignored, whatever it sends.
      if (!frame || event.source === null || event.source !== frame.contentWindow) return
      if (!isHeightMessage(event.data) || event.data.id !== id) return
      setHeight(Math.min(Math.max(Math.ceil(event.data.height), 0), MAIL_FRAME_MAX_HEIGHT))
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [id])

  const frameHeight = height === null ? undefined : `${height}px`
  return (
    <div className={cn('min-w-0', className)}>
      {srcDoc === null ? (
        <div aria-hidden className="h-24" />
      ) : (
        <iframe
          ref={frameRef}
          title={title}
          sandbox={MAIL_FRAME_SANDBOX}
          srcDoc={srcDoc}
          referrerPolicy="no-referrer"
          className="block w-full border-0 bg-transparent"
          style={{ height: frameHeight, colorScheme: theme.colorScheme }}
        />
      )}
    </div>
  )
}
